import { AddContactSlideover } from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import { ContactsGeneralSearch } from "@/components/features/contact/ContactsGeneralSearch";
import { AppModal, AppPageShell, Button, Text } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useSearchObject } from "@/hooks/useSearchObject";
import { searchContactsCountQueryOptions } from "@/lib/data/react-query/contacts/contact";
import { getSearchContactsCount } from "@/lib/data/requests";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { cn } from "@/lib/utils/cn";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { PlusCircle, SlidersHorizontal, X } from "lucide-react";
import { useMemo, useState } from "react";
import ContactsSearchBar from "./ContactsSearchBar/ContactsSearchBar";
import { ContactsTable } from "./ContactsTable/ContactsTable";

export function ContactsListView() {
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [contactCount, setContactCount] = useState<number>();
  const [draftSearchObject, setDraftSearchObject] = useState<{
    [key: string]: string[];
  }>({});
  const debouncedContactSearch = useDebouncedValue(contactSearch, 250);
  const { hasActionPermission } = useUser();
  const {
    searchObject,
    addSearchObjectValue,
    removeSearchObjectValue,
    clearSearchObject,
    replaceSearchObject,
  } = useSearchObject({
    syncWithSearchParams: true,
    persistSearchObject: true,
  });
  const activeFilterCount = Object.values(searchObject).reduce(
    (count, values) => count + values.length,
    0,
  );
  const draftContactSearchObject = useMemo(
    () =>
      debouncedContactSearch
        ? { ...draftSearchObject, search: [debouncedContactSearch] }
        : draftSearchObject,
    [debouncedContactSearch, draftSearchObject],
  );
  const { data: draftContactsData, isFetching: isDraftContactsFetching } =
    useQuery({
      ...searchContactsCountQueryOptions(draftContactSearchObject),
      enabled: isMoreFiltersOpen,
    });
  const draftContactCount = getSearchContactsCount(draftContactsData);
  const addDraftSearchObjectValue = (key: string, value: string) => {
    setDraftSearchObject((current) => {
      const values = current[key] ?? [];
      return values.includes(value)
        ? current
        : { ...current, [key]: [...values, value] };
    });
  };
  const removeDraftSearchObjectValue = (key: string, value: string) => {
    setDraftSearchObject((current) => {
      const values = (current[key] ?? []).filter((item) => item !== value);
      if (values.length > 0) return { ...current, [key]: values };
      return Object.fromEntries(
        Object.entries(current).filter(([currentKey]) => currentKey !== key),
      );
    });
  };
  const openMoreFilters = () => {
    setDraftSearchObject(searchObject);
    setIsMoreFiltersOpen(true);
  };

  return (
    <AppPageShell className="h-full min-h-0 overflow-hidden">
      <Text as="h1" variant="title" className="mb-6 shrink-0">
        Contacts
      </Text>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <ContactsTable
          chrome="matterDashboard"
          scrollableBody
          tableName={tanstackTableNames.contacts}
          searchBar={
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <ContactsGeneralSearch
                value={contactSearch}
                onChange={setContactSearch}
                itemCount={contactCount}
              />
              <ContactsSearchBar
                searchObject={searchObject}
                addSearchObjectValue={addSearchObjectValue}
                removeSearchObjectValue={removeSearchObjectValue}
                layout="quick"
              />
              <div
                className={cn(
                  "relative inline-flex",
                  activeFilterCount > 0 &&
                    "h-8 overflow-hidden rounded-full border",
                )}
              >
                <Button
                  type="button"
                  variant={activeFilterCount > 0 ? "ghost" : "outline"}
                  aria-label="All filters"
                  title="All filters"
                  className={cn(
                    "h-8 px-3 text-xs font-medium",
                    activeFilterCount > 0 ? "rounded-none" : "rounded-full",
                  )}
                  onClick={() => {
                    openMoreFilters();
                  }}
                >
                  <SlidersHorizontal />
                  All filters
                  {activeFilterCount > 0 ? (
                    <span className="bg-destructive text-destructive-foreground min-w-5 rounded-full px-1 py-0.5 text-center text-xs leading-none">
                      {activeFilterCount}
                    </span>
                  ) : null}
                </Button>
                {activeFilterCount > 0 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    aria-label="Clear filters"
                    title="Clear filters"
                    className="h-8 w-8 rounded-none border-l p-0"
                    onClick={() => {
                      void clearSearchObject();
                    }}
                  >
                    <X />
                  </Button>
                ) : null}
              </div>
            </div>
          }
          searchObject={searchObject}
          generalSearch={debouncedContactSearch}
          onRowCountChange={setContactCount}
          onRowClick={(row) => {
            const contactId = row.original.id;
            if (!contactId) return;
            void navigate({ to: `/contacts/${contactId}` });
          }}
          tableButtons={
            hasActionPermission(
              ACTION_PERMISSION_KEYS.contact,
              ACTION_PERMISSION_VALUES.create,
            ) && (
              <Button
                variant="foreground"
                size="md"
                onClick={() => {
                  setIsCreating(true);
                }}
                data-testid="create-contact-button"
              >
                <PlusCircle className="h-4 w-4" />
                <span className="hidden md:ml-2 md:block">Create contact</span>
              </Button>
            )
          }
        />
      </div>
      <AppModal
        open={isMoreFiltersOpen}
        onOpenChange={setIsMoreFiltersOpen}
        title="All filters"
        description="Choose filters, then apply them to the contacts table."
        size="lg"
        bodyContentClassName="p-0"
        footer={
          <div className="flex w-full items-center justify-between gap-3">
            <Button
              type="button"
              variant="link"
              onClick={() => {
                setDraftSearchObject({});
              }}
            >
              Clear all
            </Button>
            <Button
              type="button"
              variant="foreground"
              disabled={draftContactCount === undefined}
              onClick={() => {
                void (Object.keys(draftSearchObject).length
                  ? replaceSearchObject(draftSearchObject)
                  : clearSearchObject());
                setIsMoreFiltersOpen(false);
              }}
            >
              {isDraftContactsFetching || draftContactCount === undefined
                ? "Loading results..."
                : `Show ${draftContactCount} results`}
            </Button>
          </div>
        }
      >
        <div className="p-4">
          <ContactsSearchBar
            addSearchObjectValue={addDraftSearchObjectValue}
            removeSearchObjectValue={removeDraftSearchObjectValue}
            searchObject={draftSearchObject}
            layout="list"
          />
        </div>
      </AppModal>
      <AddContactSlideover
        open={isCreating}
        setOpen={setIsCreating}
        creatingMore={false}
        afterCreateFn={(res) =>
          void navigate({ to: `/contacts/${res.data.id}` })
        }
      />
    </AppPageShell>
  );
}
